import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Image, Upload, RotateCcw } from "lucide-react";
import { DEFAULT_HERO_IMAGES, HERO_LABELS, applyHeroOverrides, resolveHeroUrl } from "./PageHero";

export const HeroImagesManager = () => {
  const [overrides, setOverrides] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/settings/hero_images`).then(r => {
      try { setOverrides(JSON.parse(r.data.value) || {}); } catch { setOverrides({}); }
    }).catch(() => {});
  }, []);

  const persist = async (next) => {
    setOverrides(next);
    applyHeroOverrides(next);
    try {
      await axios.put(`${API}/settings/hero_images`, { value: JSON.stringify(next) });
      toast.success("Immagine hero aggiornata");
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const upload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await persist({ ...overrides, [key]: res.data.url });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setUploadingKey(null); }
  };

  const reset = async (key) => {
    const next = { ...overrides };
    delete next[key];
    await persist(next);
  };

  return (
    <div className="glass-card p-6 max-w-3xl mt-8" data-testid="setting-hero-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(47,216,168,0.15)" }}>
          <Image size={20} style={{ color: "var(--mm-gold)" }} />
        </div>
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>
            Immagini Hero
          </h2>
          <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>
            Personalizza le grandi immagini di sfondo in cima a ogni pagina (jpg, png, webp)
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {Object.keys(DEFAULT_HERO_IMAGES).map((key) => {
          const currentUrl = overrides[key] ? resolveHeroUrl(overrides[key]) : DEFAULT_HERO_IMAGES[key];
          const isCustom = !!overrides[key];
          return (
            <div key={key} className="flex items-center gap-4 pb-4" style={{ borderBottom: "1px solid var(--mm-border)" }} data-testid={`hero-row-${key}`}>
              <div className="w-24 h-14 flex-shrink-0 overflow-hidden" style={{ border: "1px solid var(--mm-border)" }}>
                <img src={currentUrl} alt={key} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{HERO_LABELS[key]}</p>
                <p className="text-xs" style={{ color: isCustom ? "var(--mm-gold)" : "var(--mm-text-muted)" }}>{isCustom ? "Personalizzata" : "Default"}</p>
              </div>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                ref={el => (fileRefs.current[key] = el)}
                onChange={e => { upload(key, e.target.files?.[0]); e.target.value = ""; }} />
              <button onClick={() => fileRefs.current[key]?.click()} disabled={uploadingKey === key}
                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase transition-all hover:bg-white/5"
                style={{ border: "1px solid var(--mm-gold)", color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif", opacity: uploadingKey === key ? 0.5 : 1 }}
                data-testid={`hero-upload-${key}`}>
                <Upload size={14} /> {uploadingKey === key ? "Carico..." : "Carica"}
              </button>
              {isCustom && (
                <button onClick={() => reset(key)}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-semibold uppercase transition-all hover:bg-white/5"
                  style={{ border: "1px solid var(--mm-border)", color: "var(--mm-text-secondary)", fontFamily: "'Space Grotesk', sans-serif" }}
                  data-testid={`hero-reset-${key}`}>
                  <RotateCcw size={13} /> Default
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
